import { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface NotificationBannerProps {
  type: 'warning' | 'info';
  label: string;
  title: string;
  content: string;
  link?: { text: string; url: string };
  bannerImageUrl?: string;
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const THUMBNAILABLE_EXT = /\.(png|jpe?g|webp)$/i;

/**
 * 从公告 banner 的 storage key 推导 medium 缩略图 URL。
 *
 * 后端 /api/media/file 在缩略图 key 404 时会自动从原图生成 WebP 缩略图
 *（resize 1024px / quality 80）并回写 MinIO，因此只需请求缩略图 URL，
 * 首次访问自动生成、后续命中缓存，无需后端预生成。
 *
 * 仅对 images/ 前缀且 png/jpg/webp 格式的 key 生效；gif 和外链返回 null，
 * 由调用方 fallback 到原图。
 */
function resolveBannerThumbnailUrl(bannerImageUrl: string): string | null {
  const trimmed = bannerImageUrl.trim();
  if (!trimmed.startsWith('images/')) return null;
  if (!THUMBNAILABLE_EXT.test(trimmed)) return null;

  const lastSlash = trimmed.lastIndexOf('/');
  const dir = trimmed.slice(0, lastSlash);
  const filename = trimmed.slice(lastSlash + 1);
  const ext = filename.match(THUMBNAILABLE_EXT)![0];
  const baseName = filename.slice(0, filename.length - ext.length);
  const thumbKey = `${dir}/thumbs/${baseName}.m.webp`;
  return resolveBrowserMediaUrl(thumbKey);
}

export function NotificationBanner({
  type,
  label,
  title,
  content,
  link,
  bannerImageUrl,
  onClose,
  collapsed = false,
  onToggleCollapse,
}: NotificationBannerProps) {
  const [useOriginal, setUseOriginal] = useState(false);

  const bgColor = type === 'warning'
    ? 'bg-accent-yellow/10 border-accent-yellow/30'
    : 'bg-accent-blue/10 border-accent-blue/30';
  const badgeColor = type === 'warning'
    ? 'bg-accent-yellow/20 text-accent-yellow'
    : 'bg-accent-blue/20 text-accent-blue';

  // 折叠态：统一窄条，不渲染图片/正文/链接
  if (collapsed) {
    return (
      <div className={`${bgColor} border rounded-2xl px-4 py-2 shadow-sm`}>
        <div className="flex items-center gap-2">
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-lg ${badgeColor}`}>
            {label}
          </span>
          <p className="flex-1 min-w-0 text-sm text-light-text truncate">
            <span className="font-medium">{title}</span>
          </p>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              title="展开"
              className="shrink-0 text-light-muted hover:text-light-text transition-colors p-1"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            title="关闭"
            className="shrink-0 text-light-muted hover:text-light-text transition-colors p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Banner 模式：纯图片横幅，无背景色/边框/padding
  if (bannerImageUrl) {
    const fallbackUrl = resolveBrowserMediaUrl(bannerImageUrl);
    const thumbUrl = useOriginal ? null : resolveBannerThumbnailUrl(bannerImageUrl);
    const imgEl = (
      <img
        src={thumbUrl ?? fallbackUrl}
        alt={title}
        className="w-full rounded-2xl object-cover"
        onError={() => {
          if (!useOriginal) {
            setUseOriginal(true);
          }
        }}
      />
    );
    return (
      <div className="relative">
        {link ? (
          <a href={link.url} target="_blank" rel="noopener noreferrer" className="block">
            {imgEl}
          </a>
        ) : (
          imgEl
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title="折叠"
            className="absolute top-2 left-2 p-1.5 rounded-lg bg-black/40 text-white hover:bg-black/60 transition-colors backdrop-blur-sm"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/40 text-white hover:bg-black/60 transition-colors backdrop-blur-sm"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // 文字卡片模式（原有逻辑）
  const textColor = type === 'warning' ? 'text-accent-yellow' : 'text-accent-blue';

  return (
    <div className={`${bgColor} border rounded-2xl p-4 shadow-sm`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${badgeColor}`}>
              {label}
            </span>
          </div>
          <p className="text-sm text-light-text">
            <span className="font-medium">{title}</span>
            {content}
            {link && (
              <a href={link.url} className={`${textColor} underline ml-1 font-medium`} target="_blank" rel="noopener noreferrer">
                {link.text}
              </a>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1 ml-4">
          {onToggleCollapse && (
            <button onClick={onToggleCollapse} title="折叠" className="text-light-muted hover:text-light-text transition-colors p-1">
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="text-light-muted hover:text-light-text transition-colors p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
